import {
    endOfDay,
    endOfMonth,
    endOfWeek,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
    type Locale,
} from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

import { zoneDateUtils, ZonedDate } from "@@/utilities/zoned-date";
import type { Timezone } from "./timezone";
import { tz } from "./timezone";

/**
 * Get start of day in given timezone unsure what is really does?
 * default to "Europe/Stockholm"
 *
 * @param {Date} date - UTC
 * @param {string} [timezone="Europe/Stockholm"]
 * @return {*}  {Date} - Returns UTC
 */
function startOfDayZoned(date: Date, timezone?: Timezone): Date;
function startOfDayZoned(date: ZonedDate, timezone?: Timezone): ZonedDate;
function startOfDayZoned(
    date: Date | ZonedDate,
    timezone?: Timezone,
): Date | ZonedDate;
function startOfDayZoned(
    date: Date | ZonedDate,
    timezone = tz.EuropeStockholm,
): Date | ZonedDate {
    const _date =
        date instanceof ZonedDate ? zoneDateUtils.fromZonedDate(date) : date;
    const zonedDate = toZonedTime(_date, timezone);
    const zonedStart = startOfDay(zonedDate);
    const result = fromZonedTime(zonedStart, timezone);
    return date instanceof ZonedDate
        ? zoneDateUtils.toZonedTime(result, timezone)
        : result;
}
/**
 * Get end of day in given timezone \
 * default to "Europe/Stockholm"
 *
 * @param {Date} date
 * @param {string} [timezone="Europe/Stockholm"]
 * @return {*}  {Date}
 */
function endOfDayZoned(date: Date, timezone?: Timezone): Date;
function endOfDayZoned(date: ZonedDate, timezone?: Timezone): ZonedDate;
function endOfDayZoned(
    date: Date | ZonedDate,
    timezone?: Timezone,
): Date | ZonedDate;
function endOfDayZoned(
    date: Date | ZonedDate,
    timezone = tz.EuropeStockholm,
): Date | ZonedDate {
    const _date =
        date instanceof ZonedDate ? zoneDateUtils.fromZonedDate(date) : date;
    const zonedDate = toZonedTime(_date, timezone);
    const zonedStart = endOfDay(zonedDate);
    const result = fromZonedTime(zonedStart, timezone);
    return date instanceof ZonedDate
        ? zoneDateUtils.toZonedTime(result, timezone)
        : result;
}

/**
 * Get end of week in given timezone \
 * default to "Europe/Stockholm"
 *
 * @param {Date} date
 * @param {string} [timezone="Europe/Stockholm"]
 * @return {*}  {Date}
 */
type WeekOptions = {
    locale?: Locale | undefined;
    weekStartsOn?: 0 | 4 | 2 | 1 | 3 | 5 | 6 | undefined;
};
function startOfWeekZoned(
    date: Date,
    timezone?: Timezone,
    options?: WeekOptions,
): Date;
function startOfWeekZoned(
    date: ZonedDate,
    timezone?: Timezone,
    options?: WeekOptions,
): ZonedDate;
function startOfWeekZoned(
    date: Date | ZonedDate,
    timezone?: Timezone,
    options?: WeekOptions,
): Date | ZonedDate;
function startOfWeekZoned(
    date: Date | ZonedDate,
    timezone = tz.EuropeStockholm,
    options: WeekOptions = {},
): Date | ZonedDate {
    const weekStartsOn = options?.weekStartsOn || 1;
    const _date =
        date instanceof ZonedDate ? zoneDateUtils.fromZonedDate(date) : date;
    const zonedDate = toZonedTime(_date, timezone);
    const zonedStart = startOfWeek(zonedDate, { ...options, weekStartsOn });
    const result = fromZonedTime(zonedStart, timezone);
    return date instanceof ZonedDate
        ? zoneDateUtils.toZonedTime(result, timezone)
        : result;
}

/**
 * Get end of month in given timezone \
 * default to "Europe/Stockholm"
 *
 * @param {Date} date
 * @param {string} [timezone="Europe/Stockholm"]
 * @return {*}  {Date}
 */

function startOfMonthZoned(date: Date, timezone?: Timezone): Date;
function startOfMonthZoned(date: ZonedDate, timezone?: Timezone): ZonedDate;
function startOfMonthZoned(
    date: Date | ZonedDate,
    timezone?: Timezone,
): Date | ZonedDate;
function startOfMonthZoned(
    date: Date | ZonedDate,
    timezone = tz.EuropeStockholm,
): Date | ZonedDate {
    const _date =
        date instanceof ZonedDate ? zoneDateUtils.fromZonedDate(date) : date;
    const zonedDate = toZonedTime(_date, timezone);
    const zonedStart = startOfMonth(zonedDate);
    const result = fromZonedTime(zonedStart, timezone);
    return date instanceof ZonedDate
        ? zoneDateUtils.toZonedTime(result, timezone)
        : result;
}

/**
 * Get start of year in given timezone \
 * default to "Europe/Stockholm"
 *
 * @param {Date} date
 * @param {string} [timezone="Europe/Stockholm"]
 * @return {*}  {Date}
 */
function startOfYearZoned(date: Date, timezone?: Timezone): Date;
function startOfYearZoned(date: ZonedDate, timezone?: Timezone): ZonedDate;
function startOfYearZoned(date: Date | ZonedDate, timezone?: Timezone): Date;
function startOfYearZoned(
    date: Date | ZonedDate,
    timezone = tz.EuropeStockholm,
): Date | ZonedDate {
    const _date =
        date instanceof ZonedDate ? zoneDateUtils.fromZonedDate(date) : date;
    const zonedDate = toZonedTime(_date, timezone);
    const zonedStart = startOfYear(zonedDate);
    const result = fromZonedTime(zonedStart, timezone);
    return date instanceof ZonedDate
        ? zoneDateUtils.toZonedTime(result, timezone)
        : result;
}
/**
 * Get end of week in given timezone \
 * default to "Europe/Stockholm"
 *
 * @param {Date} date
 * @param {string} [timezone="Europe/Stockholm"]
 * @return {*}  {Date}
 */
function endOfWeekZoned(
    date: Date,
    timezone?: Timezone,
    options?: WeekOptions,
): Date | ZonedDate;
function endOfWeekZoned(
    date: ZonedDate,
    timezone?: Timezone,
    options?: WeekOptions,
): Date | ZonedDate;
function endOfWeekZoned(
    date: Date | ZonedDate,
    timezone?: Timezone,
    options?: WeekOptions,
): Date | ZonedDate;
function endOfWeekZoned(
    date: Date | ZonedDate,
    timezone = tz.EuropeStockholm,
    options?: WeekOptions,
): Date | ZonedDate {
    const weekStartsOn = options?.weekStartsOn || 1;
    const _date =
        date instanceof ZonedDate ? zoneDateUtils.fromZonedDate(date) : date;
    const zonedDate = toZonedTime(_date, timezone);
    const zonedStart = endOfWeek(zonedDate, {
        ...(options ?? {}),
        weekStartsOn,
    });
    const result = fromZonedTime(zonedStart, timezone);
    return date instanceof ZonedDate
        ? zoneDateUtils.toZonedTime(result, timezone)
        : result;
}
/**
 * Get end of month in given timezone \
 * default to "Europe/Stockholm"
 *
 * @param {Date} date
 * @param {string} [timeZone="Europe/Stockholm"]
 * @return {*}  {Date}
 */
function endOfMonthZoned(date: Date, timezone?: Timezone): Date;
function endOfMonthZoned(date: ZonedDate, timezone?: Timezone): ZonedDate;
function endOfMonthZoned(
    date: Date | ZonedDate,
    timezone?: Timezone,
): Date | ZonedDate;
function endOfMonthZoned(
    date: Date | ZonedDate,
    timezone = tz.EuropeStockholm,
): Date | ZonedDate {
    const _date =
        date instanceof ZonedDate ? zoneDateUtils.fromZonedDate(date) : date;
    const zonedDate = toZonedTime(_date, timezone);
    const zonedStart = endOfMonth(zonedDate);
    const result = fromZonedTime(zonedStart, timezone);
    return date instanceof ZonedDate
        ? zoneDateUtils.toZonedTime(result, timezone)
        : result;
}

export {
    endOfDayZoned,
    endOfMonthZoned,
    endOfWeekZoned,
    startOfDayZoned,
    startOfMonthZoned,
    startOfWeekZoned,
    startOfYearZoned,
};
